/**
 * DAT1 sniffing — Fallout 1's archive format.
 *
 * DAT1 is the mirror image of DAT2: the directory structure sits at the START
 * of the file, not the end, and every integer is big-endian. There is no magic
 * number, so we validate structurally: the first 16 bytes must parse as a
 * valid assoc_array header whose `datasize` is 0 (root entries are directory
 * names, not file records), and the directory names that follow must be
 * printable ASCII.
 *
 * Layout (all integers big-endian):
 *
 *   [ root header: 4×u32be ]
 *     size      — number of top-level directories
 *     max       — capacity (>= size)
 *     datasize  — 0 (root carries no per-entry data)
 *     list_ptr  — meaningless on disk, ignored
 *
 *   [ for each directory: 1 byte name_len + name_len bytes name ]
 *
 *   [ per-directory assoc_array headers + file entries + file data ... ]
 *
 * FO1's master.dat has ~20 top-level directories (art, color, data, maps,
 * misc, premade, sound, text, …) and is ~300MB. critter.dat follows the same
 * format.
 */

import { DatError, sniffArchive } from './dat';

export interface Dat1Info {
  dirCount: number;
  /** First directory name in the root, e.g. "color". Proof of life. */
  firstDir: string;
  /** All root directory names we could read from the header. */
  dirs: string[];
}

const MIN_SIZE = 32;
const PLAUSIBLE_MIN = 1024;
const MAX_DIR_COUNT = 200;

function readBE32(buf: Uint8Array, off: number): number {
  return ((buf[off] << 24) | (buf[off + 1] << 16) | (buf[off + 2] << 8) | buf[off + 3]) >>> 0;
}

function isPrintableAscii(b: number): boolean {
  return b >= 0x20 && b <= 0x7e;
}

/**
 * Non-throwing probe: returns true if the first bytes look like a DAT1 header.
 * Used by the import pipeline to distinguish FO1 from FO2 without committing
 * to either.
 */
export async function sniffDat1(blob: Blob): Promise<boolean> {
  if (blob.size < MIN_SIZE) return false;
  const head = new Uint8Array(await blob.slice(0, 512).arrayBuffer());
  const size = readBE32(head, 0);
  const max = readBE32(head, 4);
  const datasize = readBE32(head, 8);

  if (size === 0 || size > MAX_DIR_COUNT) return false;
  if (max < size) return false;
  if (datasize !== 0) return false;

  let pos = 16;
  for (let i = 0; i < Math.min(size, 5); i++) {
    if (pos >= head.length) return false;
    const nameLen = head[pos++];
    if (nameLen === 0 || nameLen > 200) return false;
    if (pos + nameLen > head.length) return false;
    for (let j = 0; j < nameLen; j++) {
      if (!isPrintableAscii(head[pos + j])) return false;
    }
    pos += nameLen;
  }
  return true;
}

export async function readDat1Info(blob: Blob): Promise<Dat1Info> {
  if (blob.size < MIN_SIZE || blob.size < PLAUSIBLE_MIN) {
    throw new DatError(`That file is only ${blob.size} bytes — far too small to be a Fallout 1 .dat.`);
  }

  const headLen = Math.min(blob.size, 4096);
  const head = new Uint8Array(await blob.slice(0, headLen).arrayBuffer());

  const size = readBE32(head, 0);
  const max = readBE32(head, 4);
  const datasize = readBE32(head, 8);

  if (size === 0 || size > MAX_DIR_COUNT) {
    throw new DatError(
      `This is not a Fallout 1 DAT1 archive: its root directory count (${size}) is out of range.`
    );
  }
  if (max < size) {
    throw new DatError(
      'This is not a Fallout 1 DAT1 archive: its root capacity is less than its entry count.'
    );
  }
  if (datasize !== 0) {
    throw new DatError(
      'This is not a Fallout 1 DAT1 archive: the root datasize is not zero.'
    );
  }

  const dirs: string[] = [];
  let pos = 16;
  for (let i = 0; i < size; i++) {
    if (pos >= head.length) {
      throw new DatError('This .dat is truncated: the root directory list ends prematurely.');
    }
    const nameLen = head[pos++];
    if (nameLen === 0 || nameLen > 200) {
      throw new DatError(
        `This is not a Fallout 1 DAT1 archive: directory name ${i} has an impossible length (${nameLen}).`
      );
    }
    if (pos + nameLen > head.length) {
      throw new DatError('This .dat is truncated: a directory name extends past the buffered header.');
    }
    for (let j = 0; j < nameLen; j++) {
      if (!isPrintableAscii(head[pos + j])) {
        throw new DatError(
          'This is not a Fallout 1 DAT1 archive: its root directory names contain non-ASCII bytes.'
        );
      }
    }
    dirs.push(String.fromCharCode(...head.subarray(pos, pos + nameLen)));
    pos += nameLen;
  }

  return { dirCount: size, firstDir: dirs[0], dirs };
}

export async function acceptDat1(blob: Blob, filename: string): Promise<Dat1Info> {
  const kind = await sniffArchive(blob);
  if (kind === 'rar') {
    throw new DatError(
      `${filename} is a .rar archive, and we cannot read those — the browser has no built-in RAR decoder. ` +
        'Extract it on your machine and drop the .dat from inside it.'
    );
  }
  if (kind === '7z' || kind === 'cab' || kind === 'gzip') {
    throw new DatError(
      `${filename} is a compressed archive we cannot read. Extract it locally and drop the .dat from inside.`
    );
  }
  if (kind === 'exe') {
    throw new DatError(
      `${filename} is a Windows executable. Run it on a desktop install and then drop the extracted files.`
    );
  }
  if (kind === 'zip') {
    throw new DatError(`${filename} is a .zip; that is handled by the zip path, not here.`);
  }
  return readDat1Info(blob);
}
